import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';

interface RevealProps {
  children: ReactNode;
  /** Stagger index — each step delays the reveal by 55ms, capped at 8. */
  index?: number;
  /**
   * Replay the animation every time the element re-enters the viewport.
   *
   * Defaults to true, so scrolling back up and coming down again plays the
   * reveals a second time rather than leaving a static page behind. Set false
   * for anything where a repeat would be distracting rather than pleasant.
   */
  replay?: boolean;
  className?: string;
}

/**
 * Fades and lifts content as it enters the viewport.
 *
 * Collapses to a plain wrapper when the visitor prefers reduced motion — the
 * content still renders, it simply arrives without movement. That check is
 * what keeps a replaying animation from becoming a vestibular problem, since
 * a repeating fade is considerably more insistent than a one-shot one.
 */
export function Reveal({ children, index = 0, replay = true, className }: RevealProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      // `once: !replay` is the only change from the original one-shot config.
      // The -80px margin holds the trigger until the element is properly past
      // the fold, which keeps a replaying reveal from re-firing on tiny scrolls.
      viewport={{ once: !replay, margin: '-80px' }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.055, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}
